'use client'

import { AdminPanelSettingsOutlined, FlagOutlined, Search, TheaterComedyOutlined } from '@mui/icons-material'
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Portal,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useGateValue, useStatsigGate } from '~/configs/statsig'
import useSetDemoMode from '~/features/demoMode/useSetDemoMode'
import { openFeatureFlagsPanel } from '~/features/featureFlags'
import useInternalAdminTools from '~/features/internalAdminTools/useInternalAdminTools'
import useIsMac from '~/hooks/useIsMac'
import { useCloaking } from '~/store/slices/currentOrg'
import { useIsDemoMode } from '~/store/slices/demoMode'
import { useMediaQuery } from '~/theme/hooks'

import { QuickSearchResultsList } from './components/QuickSearchResultsList'
import RecentlyViewedList from './components/RecentlyViewedList'
import {
  LOADING_INDICATOR_SIZE,
  LOADING_PADDING_Y,
  OVERLAY_BORDER_RADIUS,
  OVERLAY_PADDING_MOBILE,
  OVERLAY_PADDING_X,
  OVERLAY_PADDING_Y,
  RESULTS_PADDING_BOTTOM,
  RESULTS_PADDING_X,
  SEARCH_ICON_SIZE,
  SEARCH_INPUT_BORDER_RADIUS,
  SEARCH_INPUT_HEIGHT,
  SEARCH_INPUT_MAX_WIDTH
} from './constants'
import { OPEN_QUICK_SEARCH_EVENT } from './events'
import useQuickSearchData from './hooks/useQuickSearchData'
import useQuickSearchResults from './hooks/useQuickSearchResults'
import useRecentActivity from './hooks/useRecentActivity'
import useRecentActivityResults from './hooks/useRecentActivityResults'
import useTrackNavPageVisits from './hooks/useTrackNavPageVisits'
import type { FlatItem, QuickSearchCommand, SearchCategory } from './types'

/** Maps SearchCategory to the translation key under quickSearch.categories */
const CATEGORY_LABELS = {
  charger: 'categories.chargers',
  site: 'categories.sites',
  vehicle: 'categories.vehicles',
  user: 'categories.users',
  org: 'categories.organizations',
  synoplink: 'categories.synopLinks',
  solarArray: 'categories.solarArrays',
  page: 'categories.pages'
} as const

type Position = {
  top: number
  left: number
  width: number
}

type QuickSearchBarProps = {
  /**
   * When true, the visual trigger (TextField pill) is not rendered. The component
   * still mounts the keyboard shortcut and `OPEN_QUICK_SEARCH_EVENT` listeners and
   * renders the overlay when opened. Used when the trigger lives elsewhere (e.g.
   * the sidebar) and QuickSearchBar is mounted once at the layout root.
   */
  hideTrigger?: boolean
}

export default function QuickSearchBar({ hideTrigger = false }: QuickSearchBarProps = {}) {
  const t = useTranslations('quickSearch')
  const theme = useTheme()
  const router = useRouter()
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { getRecentItems, trackVisit } = useRecentActivity()

  // Check if we're on a mobile (XS) screen
  const isXsScreen = useMediaQuery((thm) => thm.breakpoints.only('xs'))

  // Derive pixel offsets from spacing constants for overlay positioning
  // theme.spacing returns rem, then multiply by 16 to convert to pixels (1rem = 16px)
  const currentPaddingX = isXsScreen ? OVERLAY_PADDING_MOBILE : OVERLAY_PADDING_X
  const currentPaddingY = isXsScreen ? OVERLAY_PADDING_MOBILE : OVERLAY_PADDING_Y
  const overlayOffsetX = parseFloat(theme.spacing(currentPaddingX)) * 16
  const overlayOffsetY = parseFloat(theme.spacing(currentPaddingY)) * 16

  const [isActive, setIsActive] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [position, setPosition] = useState<Position | null>(null)
  const isMac = useIsMac()
  const [expandedCategories, setExpandedCategories] = useState<Set<SearchCategory>>(new Set())

  // This is all the data that we will search over.
  const data = useQuickSearchData()

  useTrackNavPageVisits(data.pages, trackVisit)

  const cloak = useCloaking()

  // "Enter Demo Mode" command (`eng-10237-demo-mode` gate, and only when not already in demo
  // mode — exiting is intentionally banner-only). Entering is the safe direction (it anonymizes),
  // so the Cmd+K path triggers the reload directly without the settings page's confirm step.
  const { enabled: demoModeGateEnabled } = useStatsigGate('eng-10237-demo-mode')
  const isSynopInternal = useGateValue('synop_internal_only')
  const isDemoMode = useIsDemoMode()
  const setDemoMode = useSetDemoMode()

  // "Show/Hide internal admin tools" flips the signed-in user's sticky
  // preference for every internal admin surface at once (today the Internal
  // Admin tab on Link detail pages), so it is offered from anywhere.
  const { enabled: internalAdminToolsEnabled, toggle: toggleInternalAdminTools } = useInternalAdminTools()

  // Default overlay dimensions when no visible trigger anchors the popup (hideTrigger mode).
  // Positions the command palette horizontally centered, near the top of the viewport.
  const DEFAULT_OVERLAY_TOP = 80
  const DEFAULT_OVERLAY_WIDTH = Math.min(SEARCH_INPUT_MAX_WIDTH, 640)

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width
      })
      return
    }
    if (typeof window === 'undefined') return
    const width = Math.min(DEFAULT_OVERLAY_WIDTH, window.innerWidth - 32)
    setPosition({
      top: DEFAULT_OVERLAY_TOP,
      left: Math.max(16, (window.innerWidth - width) / 2),
      width
    })
  }, [DEFAULT_OVERLAY_WIDTH])

  const closeSearch = useCallback(() => {
    setIsActive(false)
    setQuery('')
    setActiveIndex(0)
    setPosition(null)
    setExpandedCategories(new Set())
  }, [])

  const openSearch = useCallback(() => {
    updatePosition()
    setIsActive(true)
  }, [updatePosition])

  const navigateAndClose = useCallback(
    (href: string, replace?: boolean) => {
      if (replace) router.replace(href)
      else router.push(href)
      closeSearch()
    },
    [router, closeSearch]
  )

  const handleExpandCategory = useCallback((category: SearchCategory) => {
    setExpandedCategories((prev) => new Set(prev).add(category))
    // Reset activeIndex since the flat list structure changes
    setActiveIndex(0)
    // Re-focus input so arrow keys continue to navigate results
    inputRef.current?.focus()
  }, [])

  const handleCollapseCategory = useCallback((category: SearchCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      next.delete(category)
      return next
    })
    // Reset activeIndex since the flat list shrinks and the old index may be out of bounds
    setActiveIndex(0)
    // Re-focus input so arrow keys continue to navigate results
    inputRef.current?.focus()
  }, [])

  const commands = useMemo(() => {
    const available: QuickSearchCommand[] = []

    if (demoModeGateEnabled && !isDemoMode) {
      available.push({
        id: 'demo-mode-action',
        label: t('enterDemoMode'),
        icon: <TheaterComedyOutlined fontSize="small" />,
        onSelect: () => {
          closeSearch()
          void setDemoMode(true)
        }
      })
    }

    // Synop-internal only. The label is deliberately untranslated, matching the
    // other internal-only tooling (`BackendSwitcher`).
    if (isSynopInternal) {
      available.push({
        id: 'feature-flags-action',
        label: 'Feature Flags',
        icon: <FlagOutlined fontSize="small" />,
        onSelect: () => {
          closeSearch()
          openFeatureFlagsPanel()
        }
      })
    }

    if (isSynopInternal) {
      available.push({
        id: 'internal-admin-tools-action',
        label: internalAdminToolsEnabled ? 'Hide internal admin tools' : 'Show internal admin tools',
        icon: <AdminPanelSettingsOutlined fontSize="small" />,
        onSelect: () => {
          closeSearch()
          toggleInternalAdminTools()
        }
      })
    }

    return available
  }, [
    demoModeGateEnabled,
    isDemoMode,
    isSynopInternal,
    internalAdminToolsEnabled,
    toggleInternalAdminTools,
    setDemoMode,
    closeSearch,
    t
  ])

  const results = useQuickSearchResults({
    query,
    navigateAndClose,
    cloak,
    commands,
    expandedCategories,
    onExpandCategory: handleExpandCategory,
    ...data
  })

  const recentItems = useMemo(
    () => (isActive && !query ? getRecentItems() : null),
    [isActive, query, getRecentItems]
  )
  const recentResults = useRecentActivityResults({ recentItems, navigateAndClose, pages: data.pages })

  const showRecentlyViewed = !!recentResults?.length

  // Flatten groups for keyboard navigation and selection
  const flatItems = useMemo<FlatItem[]>(
    () => (recentResults?.length ? recentResults : results.groups.flatMap((group) => group.items)),
    [recentResults, results.groups]
  )

  // Persist auto-expanded single category for the duration of the query.
  // This prevents the category from collapsing when additional categories finish loading.
  useEffect(() => {
    if (!results.isSingleCategory) return
    const singleGroup = results.groups.find((g) => !g.isLoading && g.items.length > 0)
    if (!singleGroup) return
    const category = singleGroup.category
    setExpandedCategories((prev) => {
      if (prev.has(category)) return prev
      const next = new Set(prev)
      next.add(category)
      return next
    })
  }, [results.isSingleCategory, results.groups])

  // Reset active index and expanded categories when query or recent items changes
  useEffect(() => {
    setActiveIndex(0)
    setExpandedCategories(new Set())
  }, [query, recentItems?.length])

  // Global keyboard shortcuts: Cmd+K / Ctrl+K to toggle, Escape to close
  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      const isMetaK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      if (isMetaK) {
        e.preventDefault()
        if (isActive) {
          closeSearch()
        } else {
          openSearch()
        }
      } else if (e.key === 'Escape' && isActive) {
        e.preventDefault()
        closeSearch()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isActive, closeSearch, openSearch])

  // Allow external triggers (e.g. the sidebar search pill) to open the overlay.
  useEffect(() => {
    const onOpen = () => {
      if (!isActive) openSearch()
    }
    document.addEventListener(OPEN_QUICK_SEARCH_EVENT, onOpen)
    return () => document.removeEventListener(OPEN_QUICK_SEARCH_EVENT, onOpen)
  }, [isActive, openSearch])

  // Focus input when overlay opens
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(timer)
    }
  }, [isActive])

  // Update position on window resize when active
  useLayoutEffect(() => {
    if (!isActive) return

    const handleResize = () => updatePosition()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isActive, updatePosition])

  // Lock body scroll when search overlay is open to prevent background scrolling
  useEffect(() => {
    if (!isActive) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isActive])

  const handleSelect = useCallback(
    (index: number) => {
      setActiveIndex(index)
      flatItems[index]?.onSelect()
    },
    [flatItems]
  )

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      closeSearch()
      return
    }

    if (!flatItems.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % flatItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      flatItems[activeIndex]?.onSelect()
    }
  }

  const filterCategory = results.filterCategory

  // Determine if there's an actual search term (after optional filter prefix)
  const effectiveSearchTerm = results.searchTerm
  const hasSearchTerm = !!effectiveSearchTerm.trim()

  const { loadingByCategory } = data

  // Allow already-resolved categories to render while slower categories continue loading.
  // Show a full-panel spinner only when ALL applicable API-backed categories are loading
  // and there are no non-loading result groups to render inline.
  const apiBackedCategories: SearchCategory[] = ['site', 'charger', 'vehicle', 'user', 'org', 'synoplink', 'solarArray']
  const applicableCategories: SearchCategory[] = filterCategory ? [filterCategory] : apiBackedCategories
  const allApplicableLoading = applicableCategories.every((cat) => loadingByCategory[cat])
  const hasRenderableGroups = results.groups.some((g) => !g.isLoading && g.items.length > 0)
  const isLoading = hasSearchTerm && allApplicableLoading && !hasRenderableGroups
  const showNoResults = hasSearchTerm && results.groups.length === 0 && !isLoading
  const showResults = hasSearchTerm && results.groups.length > 0 && !isLoading

  // Shared input props for both trigger and overlay input
  const searchInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Search sx={{ color: 'text.secondary', fontSize: SEARCH_ICON_SIZE }} aria-hidden="true" />
      </InputAdornment>
    )
  }
  const placeholderSx = {
    '& .MuiInputBase-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1
    }
  }

  return (
    <>
      {/* Trigger Search Bar (legacy visible trigger, hidden when sidebar owns the trigger) */}
      {hideTrigger ? null : (
        <Box
          ref={triggerRef}
          onClick={openSearch}
          sx={{
            width: '100%',
            maxWidth: SEARCH_INPUT_MAX_WIDTH,
            cursor: 'text'
          }}
        >
          <TextField
            placeholder={t('placeholder')}
            size="small"
            fullWidth
            inputProps={{ 'aria-label': t('placeholder') }}
            InputProps={{
              ...searchInputProps,
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {isMac ? '⌘+K' : 'Ctrl+K'}
                  </Typography>
                </InputAdornment>
              )
            }}
            sx={{
              pointerEvents: 'none',
              ...placeholderSx,
              '& .MuiOutlinedInput-root': {
                height: SEARCH_INPUT_HEIGHT,
                borderRadius: SEARCH_INPUT_BORDER_RADIUS,
                backgroundColor: theme.palette.background.paper
              }
            }}
          />
        </Box>
      )}

      {/* Overlay rendered via Portal - positioned exactly over trigger */}
      {isActive && position && (
        <Portal>
          {/* Backdrop */}
          <Backdrop
            open
            sx={{
              zIndex: theme.zIndex.modal,
              backdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(0, 0, 0, 0.4)'
            }}
          />

          {/* Positioned search container */}
          <ClickAwayListener onClickAway={closeSearch}>
            <Paper
              role="search"
              aria-label={t('placeholder')}
              elevation={8}
              sx={{
                position: 'fixed',
                // On mobile, position at top left with full width. On desktop, position relative to trigger
                top: isXsScreen ? 0 : position.top - overlayOffsetY,
                left: isXsScreen ? 0 : position.left - overlayOffsetX,
                width: isXsScreen ? '100%' : position.width + overlayOffsetX * 2,
                zIndex: theme.zIndex.modal + 1,
                // On mobile, no rounded corners at top since it spans the full width
                // On desktop, this component generally sits right at the top of the screen, in which case we don't want to round the top corners
                // However, when a cloaking or time zone banner is present, then we do want to round the top corners
                // since the search window that opens isn't touching the top of the screen anymore.
                borderRadius: isXsScreen
                  ? `0 0 ${OVERLAY_BORDER_RADIUS}px ${OVERLAY_BORDER_RADIUS}px`
                  : position.top - overlayOffsetY > 0
                    ? `${OVERLAY_BORDER_RADIUS}px`
                    : `0 0 ${OVERLAY_BORDER_RADIUS}px ${OVERLAY_BORDER_RADIUS}px`,
                overflow: 'hidden'
              }}
            >
              {/* Search Input */}
              <Box sx={{ py: currentPaddingY, px: currentPaddingX }}>
                <TextField
                  inputRef={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={t('placeholder')}
                  size="small"
                  fullWidth
                  autoFocus
                  inputProps={{ 'aria-label': t('placeholder') }}
                  InputProps={{
                    ...searchInputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          esc
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    ...placeholderSx,
                    '& .MuiOutlinedInput-root': {
                      height: SEARCH_INPUT_HEIGHT,
                      borderRadius: SEARCH_INPUT_BORDER_RADIUS,
                      backgroundColor: theme.palette.background.default
                    }
                  }}
                />
              </Box>

              {/* Active filter indicator */}
              {filterCategory && (
                <Box sx={{ px: currentPaddingX, pb: 1 }}>
                  <Chip
                    label={t('filterActive', { category: t(CATEGORY_LABELS[filterCategory]) })}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {
                      // Remove the filter prefix from the query, keeping only the search term
                      const colonIndex = query.indexOf(':')
                      if (colonIndex !== -1) {
                        setQuery(query.slice(colonIndex + 1).trimStart())
                      }
                    }}
                  />
                </Box>
              )}

              {/* Results Section */}
              {showRecentlyViewed && recentResults && (
                <Box sx={{ px: RESULTS_PADDING_X, pb: RESULTS_PADDING_BOTTOM, maxHeight: '60vh', overflowY: 'auto' }}>
                  <RecentlyViewedList
                    activeIndex={activeIndex}
                    results={recentResults}
                    onSelect={handleSelect}
                  />
                </Box>
              )}

              {isLoading && (
                <Box sx={{ py: LOADING_PADDING_Y, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress aria-label={t('loading')} size={LOADING_INDICATOR_SIZE} />
                </Box>
              )}

              {showNoResults && (
                <Box sx={{ py: OVERLAY_PADDING_Y, px: OVERLAY_PADDING_X }}>
                  <Typography color="text.secondary" variant="body2">
                    {t('noResultsFor', { query: effectiveSearchTerm })}
                  </Typography>
                </Box>
              )}

              {showResults && (
                <Box sx={{ px: RESULTS_PADDING_X, pb: RESULTS_PADDING_BOTTOM }}>
                  <QuickSearchResultsList
                    activeIndex={activeIndex}
                    groups={results.groups}
                    totals={results.totals}
                    expandedCategories={results.effectiveExpandedCategories}
                    isSingleCategory={results.isSingleCategory}
                    onSelect={handleSelect}
                    onCollapseCategory={handleCollapseCategory}
                  />
                </Box>
              )}
            </Paper>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  )
}
